import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
    ArrowLeft,
    ChevronRight,
    Clock,
    Shield,
    Edit2,
    X,
    Save,
    AlertCircle,
    Plus,
    Calendar
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LogbookPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [attendance, setAttendance] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('all');
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [editForm, setEditForm] = useState({
        clock_in_date: '',
        clock_in_time: '',
        clock_out_date: '',
        clock_out_time: '',
        activity_name: '',
        notes: ''
    });
    const [saving, setSaving] = useState(false);
    
    // Manual entry state
    const [addEntryDialogOpen, setAddEntryDialogOpen] = useState(false);
    const [addEntryForm, setAddEntryForm] = useState({
        clock_in_date: '',
        clock_in_time: '',
        clock_out_date: '',
        clock_out_time: '',
        activity_name: '',
        is_duty_officer: false,
        notes: ''
    });
    const [addingSaving, setAddingSaving] = useState(false);
    
    // Activities for dropdown (clock-on activities + recent calendar events)
    const [clockOnActivities, setClockOnActivities] = useState([]);
    const [recentCalendarEvents, setRecentCalendarEvents] = useState([]);
    const [loadingActivities, setLoadingActivities] = useState(false);

    useEffect(() => {
        fetchAttendance();
    }, [user?.id]);

    // Fetch clock-on activities and recent calendar events for the dropdown
    const fetchActivitiesForDropdown = async () => {
        setLoadingActivities(true);
        try {
            // Fetch clock-on activities
            const clockOnResponse = await axios.get(`${API_URL}/clock-on-activities`);
            setClockOnActivities(clockOnResponse.data || []);
            
            // Fetch calendar activities from last 7 days
            const sevenDaysAgo = new Date();
            sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
            
            const calendarResponse = await axios.get(`${API_URL}/activities`);
            const recentEvents = (calendarResponse.data || [])
                .filter(event => new Date(event.start_time) >= sevenDaysAgo)
                .sort((a, b) => new Date(b.start_time) - new Date(a.start_time));
            setRecentCalendarEvents(recentEvents);
        } catch (error) {
            console.error('Failed to fetch activities:', error);
        } finally {
            setLoadingActivities(false);
        }
    };

    const fetchAttendance = async () => {
        if (!user?.id) return;
        try {
            // Always fetch only the current user's attendance
            const response = await axios.get(`${API_URL}/attendance`, {
                params: { user_id: user.id }
            });
            setAttendance(response.data);
        } catch (error) {
            console.error('Failed to fetch attendance:', error);
            toast.error('Failed to load attendance records');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (isoString) => {
        if (!isoString) return '';
        const date = new Date(isoString);
        return date.toLocaleDateString('en-AU', { 
            weekday: 'long', 
            day: 'numeric', 
            month: 'long', 
            year: 'numeric' 
        });
    };

    const formatTime = (isoString) => {
        if (!isoString) return '';
        const date = new Date(isoString);
        return date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
    };

    const formatDateForInput = (isoString) => {
        if (!isoString) return '';
        const date = new Date(isoString);
        return date.toISOString().split('T')[0];
    };

    const formatTimeForInput = (isoString) => {
        if (!isoString) return '';
        const date = new Date(isoString);
        return date.toTimeString().slice(0, 5);
    };

    // Check if a record can be edited (within 24 hours of creation)
    const canEdit = (record) => {
        if (!record.created_at) return false;
        const createdAt = new Date(record.created_at);
        const now = new Date();
        const hoursSinceCreation = (now - createdAt) / (1000 * 60 * 60);
        return hoursSinceCreation <= 24;
    };

    // Calculate remaining edit time
    const getRemainingEditTime = (record) => {
        if (!record.created_at) return null;
        const createdAt = new Date(record.created_at);
        const now = new Date();
        const hoursSinceCreation = (now - createdAt) / (1000 * 60 * 60);
        const remainingHours = 24 - hoursSinceCreation;
        if (remainingHours <= 0) return null;
        if (remainingHours < 1) {
            return `${Math.round(remainingHours * 60)}m left to edit`;
        }
        return `${Math.round(remainingHours)}h left to edit`;
    };

    const openEditDialog = (record) => {
        setEditingRecord(record);
        setEditForm({
            clock_in_date: formatDateForInput(record.clock_in),
            clock_in_time: formatTimeForInput(record.clock_in),
            clock_out_date: record.clock_out ? formatDateForInput(record.clock_out) : '',
            clock_out_time: record.clock_out ? formatTimeForInput(record.clock_out) : '',
            activity_name: record.activity_name || '',
            notes: record.notes || ''
        });
        setEditDialogOpen(true);
    };

    const handleSaveEdit = async () => {
        if (!editingRecord) return;
        
        setSaving(true);
        try {
            // Construct the ISO strings from date and time inputs
            const clock_in = editForm.clock_in_date && editForm.clock_in_time
                ? new Date(`${editForm.clock_in_date}T${editForm.clock_in_time}`).toISOString()
                : null;
            
            const clock_out = editForm.clock_out_date && editForm.clock_out_time
                ? new Date(`${editForm.clock_out_date}T${editForm.clock_out_time}`).toISOString()
                : null;
            
            // Validate times
            if (clock_in && clock_out && new Date(clock_out) <= new Date(clock_in)) {
                toast.error('Clock out time must be after clock in time');
                setSaving(false);
                return;
            }

            const updateData = {};
            if (clock_in) updateData.clock_in = clock_in;
            if (clock_out) updateData.clock_out = clock_out;
            if (editForm.activity_name) updateData.activity_name = editForm.activity_name;
            if (editForm.notes !== undefined) updateData.notes = editForm.notes;

            await axios.put(`${API_URL}/attendance/${editingRecord.id}`, updateData);
            
            toast.success('Attendance record updated');
            setEditDialogOpen(false);
            setEditingRecord(null);
            fetchAttendance();
        } catch (error) {
            console.error('Failed to update attendance:', error);
            const message = error.response?.data?.detail || 'Failed to update record';
            toast.error(message);
        } finally {
            setSaving(false);
        }
    };

    // Open add entry dialog with default dates (yesterday's date for backdating)
    const openAddEntryDialog = () => {
        const now = new Date();
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        
        setAddEntryForm({
            clock_in_date: yesterday.toISOString().split('T')[0],
            clock_in_time: '09:00',
            clock_out_date: yesterday.toISOString().split('T')[0],
            clock_out_time: '17:00',
            activity_name: '',
            is_duty_officer: false,
            notes: ''
        });
        fetchActivitiesForDropdown();
        setAddEntryDialogOpen(true);
    };

    // Calculate minimum allowed date (7 days ago)
    const getMinDate = () => {
        const date = new Date();
        date.setDate(date.getDate() - 7);
        return date.toISOString().split('T')[0];
    };

    // Calculate maximum allowed date (today)
    const getMaxDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    const handleAddEntry = async () => {
        if (!addEntryForm.activity_name.trim()) {
            toast.error('Please select an activity');
            return;
        }
        if (!addEntryForm.clock_in_date || !addEntryForm.clock_in_time) {
            toast.error('Please enter clock in date and time');
            return;
        }
        if (!addEntryForm.clock_out_date || !addEntryForm.clock_out_time) {
            toast.error('Please enter clock out date and time');
            return;
        }

        setAddingSaving(true);
        try {
            const clock_in = new Date(`${addEntryForm.clock_in_date}T${addEntryForm.clock_in_time}`).toISOString();
            const clock_out = new Date(`${addEntryForm.clock_out_date}T${addEntryForm.clock_out_time}`).toISOString();

            // Validate times
            if (new Date(clock_out) <= new Date(clock_in)) {
                toast.error('Clock out time must be after clock in time');
                setAddingSaving(false);
                return;
            }

            await axios.post(`${API_URL}/attendance/manual-entry`, {
                clock_in,
                clock_out,
                activity_name: addEntryForm.activity_name,
                is_duty_officer: addEntryForm.is_duty_officer,
                notes: addEntryForm.notes || 'Manually added entry'
            });

            toast.success('Attendance entry added successfully');
            setAddEntryDialogOpen(false);
            setAddEntryForm({
                clock_in_date: '',
                clock_in_time: '',
                clock_out_date: '',
                clock_out_time: '',
                activity_name: '',
                is_duty_officer: false,
                notes: ''
            });
            fetchAttendance();
        } catch (error) {
            console.error('Failed to add attendance entry:', error);
            const message = error.response?.data?.detail || 'Failed to add entry';
            toast.error(message);
        } finally {
            setAddingSaving(false);
        }
    };

    // Calculate summary statistics
    const calculateSummary = () => {
        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();

        let monthTraining = 0;
        let monthOperations = 0;
        let monthOther = 0;
        let allTimeTraining = 0;
        let allTimeOperations = 0;
        let allTimeOther = 0;

        attendance.forEach(record => {
            const hours = record.duration_hours || 0;
            const recordDate = new Date(record.clock_in);
            const isCurrentMonth = recordDate.getMonth() === currentMonth && recordDate.getFullYear() === currentYear;

            // Categorize based on activity name
            const activityName = (record.activity_name || '').toLowerCase();
            let category = 'other';
            if (activityName.includes('training') || activityName.includes('course')) {
                category = 'training';
            } else if (activityName.includes('storm') || activityName.includes('flood') || activityName.includes('rescue') || activityName.includes('search') || activityName.includes('operations') || activityName.includes('management')) {
                category = 'operations';
            }

            if (isCurrentMonth) {
                if (category === 'training') monthTraining += hours;
                else if (category === 'operations') monthOperations += hours;
                else monthOther += hours;
            }

            if (category === 'training') allTimeTraining += hours;
            else if (category === 'operations') allTimeOperations += hours;
            else allTimeOther += hours;
        });

        return {
            month: {
                training: Math.round(monthTraining),
                operations: Math.round(monthOperations),
                other: Math.round(monthOther),
                total: Math.round(monthTraining + monthOperations + monthOther)
            },
            allTime: {
                training: Math.round(allTimeTraining),
                operations: Math.round(allTimeOperations),
                other: Math.round(allTimeOther),
                total: Math.round(allTimeTraining + allTimeOperations + allTimeOther)
            }
        };
    };

    const summary = calculateSummary();
    const currentMonthName = new Date().toLocaleDateString('en-AU', { month: 'long' });

    // Filter records based on tab
    const filteredRecords = attendance.filter(record => {
        if (activeTab === 'all') return true;
        if (activeTab === 'duty_officer') return record.is_duty_officer;
        return true;
    });

    return (
        <div className="min-h-screen bg-background" data-testid="logbook-page">
            {/* Header */}
            <header className="bg-primary text-white sticky top-0 z-30">
                <div className="flex items-center justify-between h-14 px-4">
                    <div className="flex items-center">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate('/portal')}
                            className="text-white hover:bg-white/20 mr-2"
                            data-testid="logbook-back-btn"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </Button>
                        <h1 className="text-lg font-semibold">My Logbook</h1>
                    </div>
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={openAddEntryDialog}
                        className="bg-white/20 hover:bg-white/30 text-white border-white/30"
                        data-testid="add-entry-btn"
                    >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Entry
                    </Button>
                </div>
            </header>

            <div className="p-4 space-y-4">
                {/* Summary Section */}
                <Card>
                    <CardContent className="p-4">
                        <h2 className="text-sm font-semibold text-muted-foreground mb-3">Summary of your attendance</h2>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b">
                                        <th className="text-left py-2 font-medium text-muted-foreground">CATEGORY</th>
                                        <th className="text-right py-2 font-medium text-muted-foreground">MONTH ({currentMonthName.toUpperCase()})</th>
                                        <th className="text-right py-2 font-medium text-muted-foreground">ALL TIME</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="border-b">
                                        <td className="py-2">Training</td>
                                        <td className="text-right py-2">{summary.month.training} hrs</td>
                                        <td className="text-right py-2">{summary.allTime.training} hrs</td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="py-2">Operations</td>
                                        <td className="text-right py-2">{summary.month.operations} hrs</td>
                                        <td className="text-right py-2">{summary.allTime.operations} hrs</td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="py-2">Other</td>
                                        <td className="text-right py-2">{summary.month.other} hrs</td>
                                        <td className="text-right py-2">{summary.allTime.other} hrs</td>
                                    </tr>
                                    <tr className="font-semibold">
                                        <td className="py-2">Total</td>
                                        <td className="text-right py-2">{summary.month.total} hrs</td>
                                        <td className="text-right py-2">{summary.allTime.total} hrs</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>

                {/* Info about editing */}
                <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                    <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                        You can edit your attendance records within 24 hours of creation. Look for the edit icon on recent entries.
                    </p>
                </div>

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="w-full">
                        <TabsTrigger value="all" className="flex-1" data-testid="tab-all">
                            All Records
                        </TabsTrigger>
                        <TabsTrigger value="duty_officer" className="flex-1" data-testid="tab-duty-officer">
                            Duty Officer
                        </TabsTrigger>
                    </TabsList>
                </Tabs>

                {/* Attendance Records */}
                <Card>
                    <CardContent className="p-0">
                        <div className="p-4 border-b">
                            <h2 className="text-sm font-semibold text-muted-foreground">Your attendance records</h2>
                        </div>

                        {loading ? (
                            <div className="p-8 text-center text-muted-foreground">
                                <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
                                <p className="mt-2">Loading...</p>
                            </div>
                        ) : filteredRecords.length === 0 ? (
                            <div className="p-8 text-center text-muted-foreground">
                                <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                <p>No attendance records found</p>
                            </div>
                        ) : (
                            <div className="divide-y">
                                {filteredRecords.map((record) => {
                                    const editable = canEdit(record);
                                    const editTimeRemaining = getRemainingEditTime(record);
                                    
                                    return (
                                        <div 
                                            key={record.id} 
                                            className="p-4 hover:bg-muted/30 transition-colors"
                                            data-testid={`record-${record.id}`}
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <p className="font-medium">
                                                            {record.activity_name || record.event_name || 'General Attendance'}
                                                        </p>
                                                        {record.is_duty_officer && (
                                                            <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                                                                <Shield className="w-3 h-3 mr-1" />
                                                                Duty Officer
                                                            </Badge>
                                                        )}
                                                        {record.auto_clocked_out && (
                                                            <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                                                                Auto-clocked
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-muted-foreground mt-1">
                                                        {formatDate(record.clock_in)} - {formatTime(record.clock_in)}
                                                        {record.clock_out && ` - ${formatTime(record.clock_out)}`}
                                                    </p>
                                                    {record.duration_hours && (
                                                        <p className="text-sm text-primary font-medium mt-1">
                                                            {record.duration_hours.toFixed(1)} hours
                                                        </p>
                                                    )}
                                                    {editable && editTimeRemaining && (
                                                        <p className="text-xs text-muted-foreground mt-1">
                                                            {editTimeRemaining}
                                                        </p>
                                                    )}
                                                </div>
                                                {editable ? (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => openEditDialog(record)}
                                                        className="text-muted-foreground hover:text-primary"
                                                        data-testid={`edit-btn-${record.id}`}
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                    </Button>
                                                ) : (
                                                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Edit Dialog */}
            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Edit Attendance Record</DialogTitle>
                        <DialogDescription>
                            Update the times for this attendance entry. Changes can only be made within 24 hours of creation.
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Activity</Label>
                            <Input
                                value={editForm.activity_name}
                                onChange={(e) => setEditForm(prev => ({ ...prev, activity_name: e.target.value }))}
                                placeholder="Activity name"
                                data-testid="edit-activity-name"
                            />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <Label>Clock In Date</Label>
                                <Input
                                    type="date"
                                    value={editForm.clock_in_date}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, clock_in_date: e.target.value }))}
                                    data-testid="edit-clock-in-date"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Clock In Time</Label>
                                <Input
                                    type="time"
                                    value={editForm.clock_in_time}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, clock_in_time: e.target.value }))}
                                    data-testid="edit-clock-in-time"
                                />
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <Label>Clock Out Date</Label>
                                <Input
                                    type="date"
                                    value={editForm.clock_out_date}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, clock_out_date: e.target.value }))}
                                    data-testid="edit-clock-out-date"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Clock Out Time</Label>
                                <Input
                                    type="time"
                                    value={editForm.clock_out_time}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, clock_out_time: e.target.value }))}
                                    data-testid="edit-clock-out-time"
                                />
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Notes (optional)</Label>
                            <Input
                                value={editForm.notes}
                                onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                                placeholder="Add any notes..."
                                data-testid="edit-notes"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditDialogOpen(false)} disabled={saving}>
                            Cancel
                        </Button>
                        <Button onClick={handleSaveEdit} disabled={saving} data-testid="save-edit-btn">
                            {saving ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Entry Dialog */}
            <Dialog open={addEntryDialogOpen} onOpenChange={setAddEntryDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Calendar className="w-5 h-5" />
                            Add Manual Entry
                        </DialogTitle>
                        <DialogDescription>
                            Add a timecard entry for a past activity. You can backdate entries up to 7 days.
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Activity *</Label>
                            {loadingActivities ? (
                                <div className="h-10 flex items-center justify-center border rounded-md bg-muted/50">
                                    <span className="text-sm text-muted-foreground">Loading activities...</span>
                                </div>
                            ) : (
                                <Select
                                    value={addEntryForm.activity_name}
                                    onValueChange={(value) => setAddEntryForm(prev => ({ ...prev, activity_name: value }))}
                                >
                                    <SelectTrigger data-testid="add-activity-select">
                                        <SelectValue placeholder="Select an activity" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-[300px]">
                                        {/* Clock-on Activities */}
                                        {clockOnActivities.map((activity) => (
                                            <SelectItem key={activity.id} value={activity.name}>
                                                {activity.name}
                                            </SelectItem>
                                        ))}
                                        
                                        {/* Recent Calendar Events (last 7 days) */}
                                        {recentCalendarEvents.length > 0 && (
                                            <>
                                                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground border-t mt-1 pt-2">
                                                    Recent Calendar Events
                                                </div>
                                                {recentCalendarEvents.map((event) => (
                                                    <SelectItem key={event.id} value={event.title}>
                                                        <div className="flex items-center gap-2">
                                                            <span>{event.title}</span>
                                                            <span className="text-xs text-muted-foreground">
                                                                ({new Date(event.start_time).toLocaleDateString('en-AU', { 
                                                                    day: 'numeric', 
                                                                    month: 'short' 
                                                                })})
                                                            </span>
                                                        </div>
                                                    </SelectItem>
                                                ))}
                                            </>
                                        )}
                                    </SelectContent>
                                </Select>
                            )}
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <Label>Clock In Date *</Label>
                                <Input
                                    type="date"
                                    value={addEntryForm.clock_in_date}
                                    onChange={(e) => setAddEntryForm(prev => ({ ...prev, clock_in_date: e.target.value }))}
                                    min={getMinDate()}
                                    max={getMaxDate()}
                                    data-testid="add-clock-in-date"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Clock In Time *</Label>
                                <Input
                                    type="time"
                                    value={addEntryForm.clock_in_time}
                                    onChange={(e) => setAddEntryForm(prev => ({ ...prev, clock_in_time: e.target.value }))}
                                    data-testid="add-clock-in-time"
                                />
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <Label>Clock Out Date *</Label>
                                <Input
                                    type="date"
                                    value={addEntryForm.clock_out_date}
                                    onChange={(e) => setAddEntryForm(prev => ({ ...prev, clock_out_date: e.target.value }))}
                                    min={getMinDate()}
                                    max={getMaxDate()}
                                    data-testid="add-clock-out-date"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Clock Out Time *</Label>
                                <Input
                                    type="time"
                                    value={addEntryForm.clock_out_time}
                                    onChange={(e) => setAddEntryForm(prev => ({ ...prev, clock_out_time: e.target.value }))}
                                    data-testid="add-clock-out-time"
                                />
                            </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="add-is-duty-officer"
                                checked={addEntryForm.is_duty_officer}
                                onCheckedChange={(checked) => setAddEntryForm(prev => ({ ...prev, is_duty_officer: checked }))}
                                data-testid="add-is-duty-officer"
                            />
                            <Label htmlFor="add-is-duty-officer" className="text-sm font-normal cursor-pointer">
                                This was a Duty Officer shift
                            </Label>
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Notes (optional)</Label>
                            <Input
                                value={addEntryForm.notes}
                                onChange={(e) => setAddEntryForm(prev => ({ ...prev, notes: e.target.value }))}
                                placeholder="Add any notes..."
                                data-testid="add-notes"
                            />
                        </div>
                        
                        <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                            <p className="text-xs text-blue-700 dark:text-blue-300">
                                <strong>Note:</strong> Manual entries are logged for audit purposes. 
                                Entries can be backdated up to 7 days to account for calendar events from previous days.
                            </p>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAddEntryDialogOpen(false)} disabled={addingSaving}>
                            Cancel
                        </Button>
                        <Button onClick={handleAddEntry} disabled={addingSaving} data-testid="confirm-add-entry-btn">
                            {addingSaving ? 'Adding...' : 'Add Entry'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
